import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateAsaasAccountDto,
  QueryAsaasAccountsDto,
  UpdateAsaasAccountDto,
} from '@control-center/shared';

import {
  createAsaasAccount,
  disableAsaasAccount,
  fetchAsaasAccount,
  fetchAsaasAccounts,
  updateAsaasAccount,
  type AsaasAccountView,
} from '../lib/asaas-accounts-api';

export const asaasAccountsKeys = {
  all: ['asaas-accounts'] as const,
  list: (filters: QueryAsaasAccountsDto) => [...asaasAccountsKeys.all, 'list', filters] as const,
  detail: (id: string) => [...asaasAccountsKeys.all, 'detail', id] as const,
};

export function useAsaasAccounts(filters: QueryAsaasAccountsDto = {}) {
  return useQuery<AsaasAccountView[]>({
    queryKey: asaasAccountsKeys.list(filters),
    queryFn: () => fetchAsaasAccounts(filters),
    staleTime: 30_000,
  });
}

export function useAsaasAccount(id: string | undefined) {
  return useQuery<AsaasAccountView>({
    queryKey: asaasAccountsKeys.detail(id ?? ''),
    queryFn: () => fetchAsaasAccount(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateAsaasAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAsaasAccountDto) => createAsaasAccount(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: asaasAccountsKeys.all });
    },
  });
}

export function useUpdateAsaasAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAsaasAccountDto }) =>
      updateAsaasAccount(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: asaasAccountsKeys.all });
      qc.invalidateQueries({ queryKey: asaasAccountsKeys.detail(vars.id) });
    },
  });
}

export function useDisableAsaasAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => disableAsaasAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: asaasAccountsKeys.all });
    },
  });
}
